// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Button,
    Link,
    Menu,
    MenuList,
    MenuPopover,
    MenuTrigger,
    TableCellActions,
    TableCellLayout,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import {
    ArrowDownloadRegular,
    BookRegular,
    CloudDismissRegular,
    CloudRegular,
    DeleteRegular,
    HatGraduationRegular,
    MoreHorizontalRegular,
} from '@fluentui/react-icons';
import { CurriculumState } from '@microsoft/vscodeedu-api';
import React, { FC } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { GitHubLogo } from '../../icons/sign-in';
import { useAppStyles } from '../../styles';
import { IntlProps, TrackedMenuItem } from '../common';

const useStyles = makeStyles({
    description: {
        paddingTop: tokens.spacingVerticalXS,
        maxHeight: '2rem',
        color: tokens.colorNeutralForeground4,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        overflowX: 'hidden',
        overflowY: 'hidden',
    },
});

// Curriculum title data grid cell properties.
export type TitleCellProps = Readonly<{
    state: CurriculumState;
    title: string;
    titleLinkUrl?: string;
    courseLinkUrl?: string;
    courseId?: string;
    description?: string;
    repoName?: string;
    onPublish: () => void;
    onUnpublish: () => void;
    onDelete: () => void;
}>;

// Curriculum title data grid cell layout.
export const TitleCell: FC<TitleCellProps> = injectIntl((props: IntlProps<TitleCellProps>) => {
    const {
        state,
        title,
        titleLinkUrl,
        courseLinkUrl,
        courseId,
        description,
        repoName,
        intl,
        onPublish,
        onUnpublish,
        onDelete,
    } = props;

    const appStyles = useAppStyles();
    const styles = useStyles();
    const navigate = useNavigate();

    return (
        <>
            <TableCellLayout>
                {titleLinkUrl !== undefined && <Link href={titleLinkUrl}>{title}</Link>}
                {titleLinkUrl === undefined && title}
                <div className={styles.description}>{description}</div>
            </TableCellLayout>
            <TableCellActions>
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <Button
                            appearance="subtle"
                            icon={<MoreHorizontalRegular />}
                            title={intl.formatMessage({
                                defaultMessage: 'Actions',
                                description: 'Menu button icon aria label',
                            })}
                        />
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            {(state === 'draft' || state === 'published') && (
                                <TrackedMenuItem trackAction="publish" icon={<CloudRegular />} onClick={onPublish}>
                                    <FormattedMessage defaultMessage="Publish" description="Menu item text" />
                                </TrackedMenuItem>
                            )}
                            {state === 'published' && courseLinkUrl !== undefined && (
                                <TrackedMenuItem
                                    trackAction="open-as-student"
                                    icon={<HatGraduationRegular />}
                                    onClick={() => (window.location.href = courseLinkUrl)}
                                >
                                    <FormattedMessage defaultMessage="Open as Student" description="Menu item text" />
                                </TrackedMenuItem>
                            )}
                            {state === 'published' && courseLinkUrl !== undefined && (
                                <TrackedMenuItem
                                    trackAction="course-info"
                                    icon={<BookRegular />}
                                    onClick={() => navigate(`/courses/${courseId}`)}
                                >
                                    <FormattedMessage defaultMessage="View course info" description="Menu item text" />
                                </TrackedMenuItem>
                            )}
                            {state === 'published' && (
                                <TrackedMenuItem
                                    trackAction="unpublish"
                                    icon={<CloudDismissRegular />}
                                    onClick={onUnpublish}
                                >
                                    <FormattedMessage defaultMessage="Unpublish" description="Menu item text" />
                                </TrackedMenuItem>
                            )}
                            {repoName !== undefined && (
                                <TrackedMenuItem
                                    trackAction="open-github"
                                    icon={<GitHubLogo />}
                                    onClick={() => window.open(`https://github.com/${repoName}`, '_blank', 'noopener')}
                                >
                                    <FormattedMessage defaultMessage="Open GitHub repo" description="Menu item text" />
                                </TrackedMenuItem>
                            )}
                            {repoName !== undefined && (
                                <TrackedMenuItem
                                    trackAction="download"
                                    icon={<ArrowDownloadRegular />}
                                    onClick={() =>
                                        window.open(
                                            `https://github.com/${repoName}/archive/refs/heads/main.zip`,
                                            '_blank',
                                            'noopener'
                                        )
                                    }
                                >
                                    <FormattedMessage defaultMessage="Download" description="Menu item text" />
                                </TrackedMenuItem>
                            )}
                            <TrackedMenuItem
                                trackAction="delete"
                                icon={<DeleteRegular />}
                                className={appStyles.menuDanger}
                                onClick={onDelete}
                            >
                                <FormattedMessage defaultMessage="Delete" description="Menu item text" />
                            </TrackedMenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            </TableCellActions>
        </>
    );
});

export default TitleCell;
